import React from 'react';
import {
  Manufacturer,
  LgmVisibility,
  VendorType,
} from '../../../services/api/domains/manufacturers/types';
import {
  GRAY_200,
  GRAY_400,
  GRAY_500,
  GRAY_700,
  GRAY_900,
} from 'theme/grayScale';
import { COLOR_DANGER } from 'theme/colors';
import { InfoTooltip } from '../../InfoTooltip';
import { LINKAGE_FLAGS } from '../../../domains/manufacturers/linkageSources';

const COLOR_SUCCESS_GLYPH = '#22c55e';

const LGM_VISIBILITY_OPTIONS: { value: LgmVisibility; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'RETAINED', label: 'Retained' },
  { value: 'ARCHIVED', label: 'Archived' },
];

// Matches fields.ts's VendorType enum (V1_3_64's CHECK constraint) —
// confirmed final 2026-08-18 (Open Question 13): single type per vendor
// (no multi-type case found in the AML's 317 rows), this 7-value list is
// the complete set of values seen there, no changes needed.
const VENDOR_TYPE_OPTIONS: { value: VendorType; label: string }[] = [
  { value: 'API Manufacturer', label: 'API Manufacturer' },
  { value: 'CDMO/CMO/CRO', label: 'CDMO/CMO/CRO' },
  { value: 'Bio/Pharma', label: 'Bio/Pharma' },
  { value: 'Specialty Pharma', label: 'Specialty Pharma' },
  { value: 'Generics (ANDA)', label: 'Generics (ANDA)' },
  { value: 'Reseller/Distributor', label: 'Reseller/Distributor' },
  { value: 'Other', label: 'Other' },
];

const FieldLabel: React.FC<{
  children: React.ReactNode;
  tooltip: React.ReactNode;
}> = ({ children, tooltip }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      fontSize: '12px',
      fontWeight: 500,
      color: GRAY_500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '4px',
    }}>
    {children}
    <InfoTooltip text={tooltip} />
  </div>
);

const selectStyle: React.CSSProperties = {
  fontSize: '14px',
  padding: '6px 8px',
  borderRadius: '4px',
  border: `1px solid ${GRAY_200}`,
  color: GRAY_900,
  backgroundColor: '#fff',
  width: '100%',
  maxWidth: '260px',
};

interface StatusSectionProps {
  manufacturer: Manufacturer;
  setManufacturer: (manufacturer: Manufacturer) => void;
  isEditMode: boolean;
}

/**
 * Manufacturer modal Status section (issue #653, Task 10, Sub-task 10.3).
 * `lgm_visibility`/`vendor_type` edits are deferred to the modal's normal
 * Save button (unlike NamesTab's immediate write) — both already have a
 * real scalar column on Manufacturer for a draft value to live in, so this
 * mirrors every other ModalFieldRenderer-driven field rather than
 * inventing a second write pattern.
 */
const StatusSection: React.FC<StatusSectionProps> = ({
  manufacturer,
  setManufacturer,
  isEditMode,
}) => {
  const registrationStatus = manufacturer.lei_registration_status;
  const entityStatus = manufacturer.lei_entity_status;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        <div>
          <FieldLabel tooltip="LGM-set visibility. Distinct from any source system's own approval/active state — a Retained supplier is deliberately visible despite having no active SAP approval. The only place this is editable.">
            LGM Visibility
          </FieldLabel>
          {isEditMode ? (
            <select
              style={selectStyle}
              value={manufacturer.lgm_visibility}
              onChange={e =>
                setManufacturer({
                  ...manufacturer,
                  lgm_visibility: e.target.value as LgmVisibility,
                })
              }>
              {LGM_VISIBILITY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ fontSize: '14px', color: GRAY_900 }}>
              {LGM_VISIBILITY_OPTIONS.find(
                opt => opt.value === manufacturer.lgm_visibility
              )?.label ?? manufacturer.lgm_visibility}
            </div>
          )}
        </div>

        <div>
          <FieldLabel tooltip="Sourced initially from the SAP AML use code and Salesforce account type; editable here and filterable in Advanced Search. No default filter is applied — every vendor type shows unless a user filters.">
            Vendor Type
          </FieldLabel>
          {isEditMode ? (
            <select
              style={selectStyle}
              value={manufacturer.vendor_type ?? ''}
              onChange={e =>
                setManufacturer({
                  ...manufacturer,
                  vendor_type: (e.target.value || null) as VendorType | null,
                })
              }>
              <option value="">Unknown</option>
              {VENDOR_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <div style={{ fontSize: '14px', color: GRAY_900 }}>
              {manufacturer.vendor_type ?? (
                <em style={{ color: GRAY_400 }}>Unknown</em>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        <div>
          <FieldLabel tooltip="This company's operating status for its Legal Entity Identifier (LEI), per GLEIF (the Global Legal Entity Identifier Foundation) — e.g. ACTIVE, INACTIVE. Kept separate from LEI Registration Status below — the two measure different things.">
            LEI Entity Status
          </FieldLabel>
          <div style={{ fontSize: '14px', color: GRAY_900 }}>
            {entityStatus ?? <em style={{ color: GRAY_400 }}>Unknown</em>}
          </div>
        </div>

        <div>
          <FieldLabel tooltip="The registration lifecycle of this company's Legal Entity Identifier (LEI), per GLEIF (the Global Legal Entity Identifier Foundation) — e.g. ISSUED, LAPSED, RETIRED. LAPSED means the LEI registration itself has not been renewed — it does NOT mean the company is defunct. Check LEI Entity Status for the company's actual operating state.">
            LEI Registration Status
          </FieldLabel>
          {/* Deliberately no active/inactive color-coding here — a LAPSED
              registration must never read as "company inactive" (Sub-task
              10.3 AC), so this renders as plain neutral text regardless of
              value. */}
          <div style={{ fontSize: '14px', color: GRAY_900 }}>
            {registrationStatus ?? <em style={{ color: GRAY_400 }}>Unknown</em>}
          </div>
        </div>
      </div>

      <div>
        <FieldLabel tooltip="Whether this manufacturer has a matched record in each external source. ✓ = linked, ✗ = not linked, – = this source isn't built into cross-source linking yet — a neutral dash rather than a red ✗, so an unchecked source never reads as a checked negative.">
          Source Linkage
        </FieldLabel>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '8px',
          }}>
          {LINKAGE_FLAGS.map(flag => {
            const value = manufacturer[flag.key] as boolean | null | undefined;
            // Mirrors ProductModal's ✓/✗ status-summary convention, but
            // with a 3rd neutral state a 2-state indicator can't express:
            // a source not yet built into cross-source linking must read
            // as "unchecked", not as a checked negative (Not Linked).
            const glyph = !flag.connected ? '–' : value ? '✓' : '✗';
            const glyphColor = !flag.connected
              ? GRAY_400
              : value
                ? COLOR_SUCCESS_GLYPH
                : COLOR_DANGER;
            return (
              <div
                key={flag.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  color: GRAY_700,
                }}>
                <span style={{ fontWeight: 'bold', color: glyphColor }}>
                  {glyph}
                </span>
                {flag.label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StatusSection;
